import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {ArrowDown} from '../../../icons';
import {ClientMemoryApi} from '../../../../api/clientMemoryApi';
import {API_URL} from '../../../../settings/local_settings';
import {APIClient} from '../../../../utils/APIClient';
import {activeBitacoraCategories, moduleGatedCategories} from '../../memory/bitacoraLayers';
import {hasBitacoraDeepLink} from '../boosterSteps';
import {isAutoEntered, useClientFacts} from '../facts/ClientFactsStore';
import {useFactsLayerLocked} from './factsLayerLock';
import {normalizeDailyCounts} from './HistorySeismograph';
import {MemoryArchiveView} from './MemoryArchiveView';
import {MemoryHistoryTray} from './MemoryHistoryTray';

/**
 * El acto Memoria: dos tarjetas y una bandeja.
 *
 * "Qué sabemos de este cliente" tiene dos respuestas de naturaleza distinta y la pantalla las
 * enfrenta en vez de mezclarlas: los **Hechos** son lo que el equipo aprobó (sale de lo que
 * cargás) y el **Historial** es lo que quedó registrado sin que nadie lo escriba — los cambios que
 * Dashbo levanta de las plataformas por API y lo que fue pasando en Dashbo. La bandeja de abajo
 * toma el color de la tarjeta elegida, así el bloque entero dice de cuál de las dos se está
 * hablando sin tener que leer un título.
 *
 * ⚠️ La bandeja de Historial es `MemoryBitacoraView` re-alojada, y la Bitácora abre con SEIS de sus
 * siete capas prendidas (todas menos `alerts`): no lista sólo cambios de plataforma. El copy de la
 * tarjeta (`ia_booster.duo.history_*`) todavía dice "Historial de plataformas" y que "no sale de lo
 * que cargás" — quedó chico respecto de lo que la bandeja muestra y de lo que el pulso cuenta.
 *
 * Los números NO son decorativos:
 *
 * - Los hechos son `library.length` del store, la MISMA lista que se dibuja abajo. En el mockup
 *   el contador decía 45, los grupos sumaban 48 y en pantalla había 19: tres números para la
 *   misma pregunta. Un número que no se puede contar en la pantalla que lo acompaña no se pone.
 *   Y si el listado NO se pudo leer, el número es un guion: un "0 aprobados" ahí afirma que la
 *   memoria está vacía cuando lo cierto es que no la pudimos leer.
 * - Los eventos son la suma de los 14 días del pulso, y lo dice el sublabel. Un "total histórico"
 *   sonaría mejor y sería inventado: ese dato no lo tenemos acá. Y el pulso cuenta EXACTAMENTE las
 *   capas que la Bitácora de abajo tiene prendidas, para que una barra de N abra un día con N
 *   filas — ver el bloque de `categories` más abajo.
 *
 * Y el estado se distingue por relleno y borde, no por opacidad: el `.62` del mockup deja la
 * tarjeta no elegida leyéndose como deshabilitada, cuando es justamente el otro botón que hay
 * que poder apretar.
 */

export const DUO_FACTS = 'facts';
export const DUO_HISTORY = 'history';

// La ventana del pulso. 14 días es lo que el sismógrafo dibuja y lo que el sublabel promete: si
// se cambia acá, cambia el copy solo (va interpolado).
const SEISMO_DAYS = 14;

/**
 * Qué cuenta el pulso: **exactamente las capas que la Bitácora de abajo tiene prendidas**.
 *
 * Es el parámetro `categories` de `client/<id>/activity-log/daily-counts/`, y sale de
 * `activeBitacoraCategories()` (`memory/bitacoraLayers.js`), la MISMA resolución que usa la
 * Bitácora para armar su request: deep-link → preferencia guardada → defaults. Mientras la bandeja
 * está abierta, cada vez que el usuario prende o apaga un chip la Bitácora lo publica
 * (`onActiveCategoriesChange`) y el pulso se vuelve a pedir.
 *
 * Que sea la misma lista no es un detalle de implementación: es lo único que sostiene la promesa
 * del gráfico — **una barra de N abre un día con N filas**. Las dos veces que se rompió fue por
 * pedir un universo distinto del que se listaba, una en cada dirección:
 *
 * 1. Sin `categories`, `_scoped()` (`core/views/clients/activity_log.py`) cae a
 *    `created_after(...).excluding_platform_changes()`: el COMPLEMENTO exacto de la capa de
 *    plataformas. Una barra de 5 abría un día con 0 filas.
 * 2. Con `categories='PLATFORM_CHANGE'` fijo, el número sí eran cambios de plataforma — pero la
 *    Bitácora abre con SEIS capas prendidas (todas menos `alerts`), así que una barra de 0 abría
 *    un día con 20 filas.
 *
 * Efecto lateral querido: cuando la capa de plataformas entra al scope, el backend bucketea los
 * días por la OCURRENCIA del cambio (`_event_datetime`) y no por la ingesta — la misma fecha por la
 * que el timeline filtra y ordena. Sin eso, el backfill —que trae ~30 días en una corrida— apilaba
 * todo en la barra de HOY.
 *
 * Desfase conocido que NO se puede cerrar desde acá: el curado de la Memoria (`MEMORY_OPS`) se
 * cuenta crudo en la serie diaria y colapsado en el timeline (rechazar 12 hechos de una tanda es 12
 * en la barra y 1 fila en la lista). Está documentado en `activity_log_daily_series.py` y es
 * heredado del sparkline del Listado de Clientes.
 */

/**
 * La URL que mira el duo.
 *
 * `location` la baja el contenedor —que ya la lee de react-router para el scroll— y es lo único que
 * AVISA cuando entra un deep-link sin cambiar de ruta. Sin la prop se cae a `window.location`, que
 * es lo que ve un montaje normal y lo que lee la Bitácora en su constructor.
 */
function urlParts(location) {
    return {
        search: location && location.search !== undefined ? location.search : window.location.search,
        hash: location && location.hash !== undefined ? location.hash : window.location.hash,
    };
}

/**
 * Con qué bandeja abre el acto.
 *
 * Normalmente son los Hechos: es la memoria propiamente dicha. Pero la Bitácora ahora vive DETRÁS
 * de la tarjeta de Historial, y tiene un contrato vivo con media app: `#bitacora` +
 * `?layers=&date_from=&date_to=` es la URL con la que el Panel, el Listado de Clientes, la
 * campanita y el Command Center la abren ya filtrada. Si el acto abriera siempre en Hechos, todos
 * esos links llevarían a una pantalla que no muestra lo que prometieron y el deep-link se perdería
 * en silencio (la Bitácora lee esos params en su CONSTRUCTOR, así que ni siquiera queda para más
 * tarde).
 */
function initialTray(location, factsLocked) {
    // La condición vive en `boosterSteps.js`, con las anclas: el contenedor la consulta para montar
    // este panel sin esperar el scroll, y acá se decide con cuál bandeja abrir. Son dos puertas de
    // la misma cerradura, y tienen que contestar igual.
    const {search, hash} = urlParts(location);
    if (hasBitacoraDeepLink(search, hash)) return DUO_HISTORY;
    // Con los Hechos fuera del plan (Memoria en OFF), lo único que esta memoria tiene es el
    // Historial: abrir en una bandeja que sólo puede decir «no está en tu plan» escondía detrás de
    // un clic lo que sí hay. La tarjeta de Hechos sigue ahí y su bandeja explica el candado.
    return factsLocked ? DUO_HISTORY : DUO_FACTS;
}

/**
 * La firma del deep-link que la URL pide AHORA, o `null` si no pide ninguno.
 *
 * Es lo que distingue las dos cosas que le pasan a esta query, que se parecen y son opuestas:
 *
 * - **Un deep-link que ENTRA** (el usuario ya estaba en el Recorrido y hace click en la campanita,
 *   en el Panel, en el Listado o en un link del Command Center). La ruta es la misma, así que nada
 *   remonta: sin esto el duo se quedaba en Hechos y la Bitácora —que ya estaba montada— nunca
 *   releía los params. El deep-link se perdía en silencio, y sólo funcionaba abriéndolo en otra
 *   pestaña.
 * - **La query que la Bitácora BORRA** cuando el usuario saca el strip de "filtros aplicados"
 *   (`clearDeepLinkParamsFromUrl`). Eso NO puede cambiar de bandeja ni desmontar nada abajo del
 *   mouse. Se descarta por dos vías, no por una: va por `history.replaceState`, que react-router ni
 *   ve —la `location` del contenedor no cambia—, y además acá sólo se reacciona a una firma
 *   PRESENTE, nunca a su desaparición.
 *
 * Lleva la `key` de la entrada de historial para que el MISMO link, clickeado dos veces, cuente
 * como dos: entre medio el usuario pudo haber limpiado los filtros.
 */
function deepLinkSignature(location) {
    const {search, hash} = urlParts(location);
    if (!hasBitacoraDeepLink(search, hash)) return null;
    return `${(location && location.key) || ''}|${search}|${hash}`;
}

export function MemoryDuo({clientId, location = null}) {
    const {t} = useTranslation();
    const {library, loading: factsLoading, loadFailed: factsFailed} = useClientFacts();
    const factsLocked = useFactsLayerLocked();
    const [selected, setSelected] = useState(() => initialTray(location, factsLocked));
    const [days, setDays] = useState(null);
    // Las capas de la Bitácora, como CSV de `categories`. Arranca resuelto por la función pura —el
    // número tiene que estar en la tarjeta antes de que la bandeja se monte— y después lo actualiza
    // la propia Bitácora cuando el usuario toca un chip.
    const [categories, setCategories] = useState(() => activeBitacoraCategories().join(','));
    // Los filtros de quién / cuenta / tipo de evento que puso el usuario en la Bitácora, ya como
    // query string. Arranca vacío (la bandeja abre sin filtros) y lo publica la propia Bitácora.
    // El pulso los recibe por la misma razón que las capas: cuenta lo que esa lista muestra.
    const [serverFilters, setServerFilters] = useState('');
    // `null` = todavía no sé si la agencia tiene el módulo de cambios en plataformas. Ver `pulse`.
    const [platformModule, setPlatformModule] = useState(null);
    const wrapRef = useRef(null);
    // Cuántas veces se re-armó la bandeja de Historial. Es la `key` de `MemoryHistoryTray`: la
    // Bitácora lee el deep-link en su CONSTRUCTOR, así que releerlo es volver a construirla. Se
    // remonta ella y no se le agrega un camino nuevo para aplicar los params, porque un segundo
    // camino contesta distinto del primero apenas alguien toque uno de los dos.
    const [trayEpoch, setTrayEpoch] = useState(0);
    // El deep-link que ya está aplicado. Arranca con el del montaje: `initialTray` y el constructor
    // de la Bitácora ya lo atendieron, y volver a atenderlo sería remontarla recién nacida.
    const appliedDeepLink = useRef(deepLinkSignature(location));

    /**
     * Entró un deep-link a la Bitácora con el Recorrido YA montado.
     *
     * Es el único camino por el que la bandeja cambia sola. Hace las tres cosas que hace un montaje
     * con deep-link, porque el usuario pidió exactamente eso: abre en Historial, re-arma la Bitácora
     * para que su constructor lea los params nuevos, y mueve el pulso a las capas del link —si no,
     * la tarjeta contaría un universo y la lista de abajo mostraría otro hasta que la Bitácora
     * publique sus capas.
     *
     * Lo que NO hace: reaccionar a que la firma desaparezca. Ver `deepLinkSignature`.
     */
    const deepLink = deepLinkSignature(location);
    useEffect(() => {
        if (!deepLink || deepLink === appliedDeepLink.current) return;
        appliedDeepLink.current = deepLink;
        setSelected(DUO_HISTORY);
        setCategories(activeBitacoraCategories().join(','));
        setTrayEpoch(epoch => epoch + 1);
    }, [deepLink]);

    // El pulso lo pide el duo, no la bandeja: el contador de la tarjeta tiene que estar antes de
    // que el usuario elija el historial. Fail-soft de punta a punta — el endpoint de daily-counts
    // puede no existir todavía y nada de esto puede romper el acto.
    useEffect(() => {
        let alive = true;
        if (!categories) {
            // Ninguna capa prendida: no hay nada que contar y no se le pega a la base. Un 0 acá
            // sería una medición que nunca se hizo (el backend contesta `base.none()`).
            setDays(null);
            return undefined;
        }
        ClientMemoryApi.getActivityDailyCounts(clientId, {
            days: SEISMO_DAYS,
            tzOffset: new Date().getTimezoneOffset(),
            categories,
            serverFilters,
        }).then((data) => {
            if (!alive) return;
            setDays(normalizeDailyCounts(data));
        }).catch(() => { if (alive) setDays(null); });
        return () => { alive = false; };
    }, [clientId, categories, serverFilters]);

    /**
     * Si la agencia tiene el módulo de Memoria de cambios en plataformas. Sale de la cobertura de la
     * Bitácora (`platform_changes.enabled`), que es el MISMO gate que aplica `_scoped()` en el
     * backend — no una heurística: es el que decide si esa capa entra al conteo.
     *
     * Hace falta porque el payload de daily-counts **no permite distinguir** "no hubo eventos" de
     * "esa capa no existe para esta agencia": las dos respuestas son `{status:'OK', days:[…14
     * ceros]}`. Fail-soft y fail-OPEN: si la cobertura no llega, se sigue asumiendo que la capa
     * cuenta (misma decisión que toma la Bitácora con el request de su timeline, para no perder la
     * capa por la carrera con la cobertura).
     */
    useEffect(() => {
        let alive = true;
        setPlatformModule(null);
        new APIClient(API_URL + 'client/' + clientId + '/activity-log/coverage/').call('GET')
            .then((data) => {
                if (!alive || !data || data.status !== 'OK' || !data.data) return;
                const platformChanges = data.data.platform_changes;
                if (platformChanges) setPlatformModule(!!platformChanges.enabled);
            })
            .catch(() => { /* sin cobertura seguimos como hasta ahora */ });
        return () => { alive = false; };
    }, [clientId]);

    /**
     * El pulso que se dibuja y se suma, o `null` cuando la respuesta no es una medición.
     *
     * Sin el módulo, el backend saca `PLATFORM_CHANGE` de lo pedido; si esa era la única capa
     * prendida se queda sin nada que contar y devuelve 14 ceros. Dibujar 14 barras mínimas y
     * afirmar «0 eventos» ahí es inventar un dato: lo honesto es un guion, el mismo criterio que
     * ya se aplica al contador de hechos cuando el listado no se pudo leer.
     */
    const pulse = useMemo(() => {
        const gated = moduleGatedCategories();
        const countable = categories.split(',').filter(Boolean)
            .filter(c => platformModule !== false || gated.indexOf(c) === -1);
        return countable.length ? days : null;
    }, [days, categories, platformModule]);

    const eventCount = useMemo(() => (
        pulse ? pulse.reduce((total, day) => total + (day.count || 0), 0) : null
    ), [pulse]);

    // `null` = "no lo sé", que no es lo mismo que cero. Mientras carga —y sobre todo si el listado
    // FALLÓ— un "0 aprobados" es una afirmación falsa sobre la memoria del cliente, y encima
    // contradice al mazo de Revisa, que con el mismo store ya está mostrando "No se pudieron cargar
    // los hechos" 300px más arriba. Misma regla que `approvedCount` del store.
    // Con los Hechos fuera del plan tampoco hay un número: el store queda vacío y CARGADO porque no
    // pide nada, y «0 aprobados» afirmaría una memoria vacía donde no hay memoria de hechos.
    const factCount = factsLoading || factsFailed || factsLocked ? null : library.length;

    // Lo que el archivo está mostrando ahora mismo (búsqueda + chips de categoría). Con un filtro
    // activo la tarjeta cuenta lo filtrado y aclara sobre cuánto — si no, afirma "45 aprobados"
    // con la lista vacía justo debajo, mientras el sismógrafo de la tarjeta hermana sí acompaña.
    const [archiveFilter, setArchiveFilter] = useState(null);
    const handleArchiveFilter = useCallback((state) => setArchiveFilter(state), []);
    const filteringFacts = Boolean(archiveFilter && archiveFilter.active) && factCount !== null;
    const factsShown = filteringFacts ? archiveFilter.shown : factCount;
    // Del MISMO predicado que la marca de la fila y el chip del archivo: tres superficies que
    // cuentan lo mismo no pueden tener tres definiciones.
    const autoCount = useMemo(() => library.filter(isAutoEntered).length, [library]);

    /**
     * Cambiar de bandeja mueve el scroll SÓLO si el duo se fue de la pantalla. El mockup hacía
     * `scrollIntoView` en cada click, incluso cuando el duo estaba justo debajo del cursor: la
     * página pegaba un salto que el usuario no pidió y que además le movía el elemento que acababa
     * de tocar.
     */
    const handleSelect = useCallback((key) => {
        setSelected(key);
        // Al salir del Historial se sueltan sus filtros. La Bitácora se desmonta y con ella los
        // selects, así que un `serverFilters` que sobreviviera dejaba el número de la TARJETA
        // filtrado —«65 eventos» en vez de 214— sin ningún control en pantalla que explicara por
        // qué. Volver al Historial re-monta la Bitácora, que publica lo suyo de nuevo.
        if (key !== DUO_HISTORY) setServerFilters('');
        const node = wrapRef.current;
        if (!node || typeof node.getBoundingClientRect !== 'function') return;
        const rect = node.getBoundingClientRect();
        const viewport = window.innerHeight || 0;
        // Estrictamente afuera. Con `<=` / `>=`, una caja degenerada (alto 0, top 0 — lo que
        // devuelve jsdom, y lo que puede devolver un nodo todavía sin layout) cuenta como "fuera
        // de vista" y dispara el salto que este guard existe para evitar.
        const outOfView = rect.bottom < 0 || rect.top > viewport;
        if (outOfView && node.scrollIntoView) {
            node.scrollIntoView({behavior: 'smooth', block: 'start'});
        }
    }, []);

    // `locked`: la tarjeta de una capa que no está en el plan. Sin unidad («— aprobados» sigue
    // hablando de hechos aprobados) y con el lede del candado en vez de describir hechos curados.
    const card = (key, count, sublabelKey, sublabelValues, locked = false) => {
        const on = selected === key;
        return (
            <button
                type="button"
                className={`iabm-duo__card -${key} ${on ? '-on' : ''}`}
                aria-pressed={on}
                onClick={() => handleSelect(key)}
            >
                <span className="iabm-duo__kicker">{t(`ia_booster.duo.${key}_title`)}</span>
                <span className="iabm-duo__number">
                    {count === null ? '—' : count}
                    {/* Con el número en guion (cargando / falló) la unidad va en plural: es la
                        forma neutra, y `null` no puede elegir sufijo. */}
                    {!locked && (
                        <small>{t(`ia_booster.duo.${key}_unit`, {count: count === null ? 0 : count})}</small>
                    )}
                </span>
                <span className="iabm-duo__lede">
                    {t(locked ? 'ia_booster.archive.locked_title' : `ia_booster.duo.${key}_lede`)}
                </span>
                {/* Los valores los pone el LLAMADOR: cada sublabel necesita los suyos, y mandarle
                    a todos el `n` de la ventana del pulso hacía que un `count` —la variable con la
                    que i18next elige el plural— quedara detrás de un valor que esa clave ni usa. */}
                {sublabelKey && (
                    <span className="iabm-duo__sub">{t(sublabelKey, sublabelValues)}</span>
                )}
                {/* La flecha ↓ sólo en la tarjeta ELEGIDA (mockup: `.duoarrow`). Es lo que ata la
                    tarjeta con la bandeja que está justo abajo: sin ella, dos cajas iguales y una
                    lista debajo no dicen cuál de las dos la produjo. En la tarjeta apagada no va,
                    porque ahí la flecha apuntaría a un contenido que no es el suyo. */}
                <span className="iabm-duo__cta">
                    {on && <ArrowDown size={12} weight="bold" aria-hidden="true"/>}
                    {t(`ia_booster.duo.${key}_cta`)}
                </span>
            </button>
        );
    };

    return (
        <div className="iabm-duo-wrap" ref={wrapRef}>
            <div className="iabm-duo">
                {/* Con la bandeja de Historial abierta, el chip «Entró solo» del archivo no está en
                    pantalla — y ahí aterrizan TODOS los deep-links `#bitacora` (el Panel, la
                    campanita, el Command Center). La tarjeta es lo único que se ve desde las dos
                    caras, así que el número vive acá (review 2026-09-09, R-08). Con un filtro
                    puesto manda el filtro: dos números en el mismo renglón compiten. */}
                {card(DUO_FACTS, factsShown,
                    filteringFacts
                        ? 'ia_booster.duo.facts_filtered'
                        : (autoCount > 0 ? 'ia_booster.duo.facts_auto' : null),
                    filteringFacts ? {total: factCount} : {count: autoCount},
                    factsLocked)}
                {card(DUO_HISTORY, eventCount,
                    eventCount === null ? null : 'ia_booster.duo.history_window',
                    {n: SEISMO_DAYS})}
            </div>
            {/* El color de la bandeja lo fijan `--tab` / `--tab-ink`, declaradas por el
                modificador `-facts` / `-history`: el borde, el degradé y el filo de arriba salen
                de la misma variable, así que no pueden quedar de dos colores distintos. */}
            <div className={`iabm-tray -${selected}`}>
                {selected === DUO_FACTS
                    ? <MemoryArchiveView clientId={clientId} onFilterState={handleArchiveFilter}
                                         factsLocked={factsLocked}/>
                    : <MemoryHistoryTray key={trayEpoch} clientId={clientId} days={pulse}
                                         onCategoriesChange={setCategories}
                                         onServerFiltersChange={setServerFilters}/>}
            </div>
        </div>
    );
}

export default MemoryDuo;
